import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import { Centrifuge } from 'centrifuge';

import { CENTRIFUGO_URL } from '../../envs';
import { getCustomersList } from '../../api';
import type {
  CentrifugoNotification,
  CentrifugoEventType,
  NotificationCallback,
  CentrifugeClient,
  CentrifugeSubscription,
  CentrifugeClientFactory,
  GPAgentResponseNotification,
  GPAgentResponseCallback,
} from '../../types';

import { CentrifugoContext } from './CentrifugoContext';

// No-op unsubscribe function for cases where subscription is not needed
const noop = () => {
  // Intentionally empty - used as no-op unsubscribe
};

const NOTIFICATION_TYPES: Record<CentrifugoEventType, 'dialogue' | 'activities'> = {
  dialogue_updated: 'dialogue',
  activities_updated: 'activities',
};

const defaultClientFactory: CentrifugeClientFactory = (url) => new Centrifuge(url) as CentrifugeClient;

/**
 * Safely creates a subscription on the Centrifugo client.
 * Returns null if subscription already exists or if creation fails.
 * This ensures Centrifugo errors never crash the main application.
 */
const safeCreateSubscription = (
  client: CentrifugeClient,
  channel: string,
  onPublication: (ctx: {data: unknown}) => void,
): CentrifugeSubscription | null => {
  try {
    // Check if subscription already exists on the client
    const existingSubscription = client.getSubscription(channel);
    if (existingSubscription) {
      return existingSubscription;
    }

    const subscription = client.newSubscription(channel);
    subscription.on('publication', onPublication);
    subscription.subscribe();
    return subscription;
  } catch (error) {
    console.warn(`[Centrifugo] Failed to create subscription for channel "${channel}":`, error);
    return null;
  }
};

/**
 * Safely removes a subscription from the Centrifugo client.
 * Errors are logged but never thrown.
 */
const safeRemoveSubscription = (client: CentrifugeClient | null, subscription: CentrifugeSubscription): void => {
  try {
    subscription.unsubscribe();
    client?.removeSubscription(subscription);
  } catch (error) {
    console.warn('[Centrifugo] Failed to remove subscription:', error);
  }
};

interface CentrifugoContextProviderProps {
  children: React.ReactNode;
  clientFactory?: CentrifugeClientFactory;
}

const CentrifugoContextProvider: React.FC<CentrifugoContextProviderProps> = ({
  children,
  clientFactory = defaultClientFactory,
}) => {
  const [assignedCustomerIds, setAssignedCustomerIds] = useState<number[]>([]);
  const clientRef = useRef<CentrifugeClient | null>(null);
  const subscriptionsRef = useRef<Map<number, CentrifugeSubscription>>(new Map());
  // Track ad-hoc subscriptions (e.g., for currently viewed customer) with reference counts
  const adHocSubscriptionsRef = useRef<Map<number, {subscription: CentrifugeSubscription; refCount: number}>>(
    new Map(),
  );
  const [isConnected, setIsConnected] = useState(false);

  const dialogueCallbacksRef = useRef<Set<NotificationCallback>>(new Set());
  const activitiesCallbacksRef = useRef<Set<NotificationCallback>>(new Set());

  // Fetch assigned customers on mount
  useEffect(() => {
    const fetchAssignedCustomers = async() => {
      try {
        const response = await getCustomersList({ my_customers: true, page_size: 200 });
        setAssignedCustomerIds(response.results.map((c) => c.id));
      } catch (error) {
        console.warn('Failed to fetch assigned customers for Centrifugo subscriptions:', error);
      }
    };

    fetchAssignedCustomers();
  }, []);

  // Handle incoming publications - dispatches to registered callbacks
  // Note: We dispatch ALL notifications to callbacks, letting the callbacks
  // decide what to do (e.g., filter by selected customer, track pending updates)
  const handlePublication = useCallback((ctx: {data: unknown}) => {
    const data = ctx.data as CentrifugoNotification | null;

    // Guard against malformed or null data
    if (!data || typeof data !== 'object' || !data.type) {
      return;
    }

    const notificationType = NOTIFICATION_TYPES[data.type];
    if (!notificationType) {
      return;
    }

    const callbacks =
      notificationType === 'dialogue' ? dialogueCallbacksRef.current : activitiesCallbacksRef.current;

    // Execute each callback safely - one failing callback shouldn't break others
    callbacks.forEach((cb) => {
      try {
        cb(data.customer_id);
      } catch (error) {
        console.warn('[Centrifugo] Callback execution failed:', error);
      }
    });
  }, []);

  // Initialize and manage WebSocket client connection
  useEffect(() => {
    if (!clientRef.current) {
      clientRef.current = clientFactory(CENTRIFUGO_URL);
      clientRef.current.on('connected', () => setIsConnected(true));
      clientRef.current.on('disconnected', () => setIsConnected(false));
      clientRef.current.on('error', (error) => {
        console.warn('[Centrifugo] Client error:', error);
      });
      clientRef.current.connect();
    }

    return () => {
      try {
        clientRef.current?.disconnect();
      } catch (error) {
        console.warn('[Centrifugo] Failed to disconnect client:', error);
      }
      clientRef.current = null;
    };
  }, [clientFactory]);

  // Manage channel subscriptions for assigned customers
  useEffect(() => {
    if (!clientRef.current || assignedCustomerIds.length === 0) return;

    const client = clientRef.current;

    assignedCustomerIds.forEach((customerId) => {
      // Skip if already subscribed via either ref
      if (subscriptionsRef.current.has(customerId) || adHocSubscriptionsRef.current.has(customerId)) {
        return;
      }

      const channel = `customer:${customerId}`;
      const subscription = safeCreateSubscription(client, channel, handlePublication);
      if (subscription) {
        subscriptionsRef.current.set(customerId, subscription);
      }
    });

    return () => {
      subscriptionsRef.current.forEach((subscription) => {
        safeRemoveSubscription(client, subscription);
      });
      subscriptionsRef.current.clear();
    };
  }, [assignedCustomerIds, handlePublication]);

  // Callback registration functions
  const onDialogueUpdated = useCallback((callback: NotificationCallback) => {
    dialogueCallbacksRef.current.add(callback);
    return () => {
      dialogueCallbacksRef.current.delete(callback);
    };
  }, []);

  const onActivitiesUpdated = useCallback((callback: NotificationCallback) => {
    activitiesCallbacksRef.current.add(callback);
    return () => {
      activitiesCallbacksRef.current.delete(callback);
    };
  }, []);

  // Subscribe to a specific customer channel (for currently viewed customer).
  // Uses reference counting for multiple subscribers.
  // Returns a cleanup function that safely unsubscribes.
  const subscribeToCustomer = useCallback(
    (customerId: number) => {
      const client = clientRef.current;

      // No client or already subscribed via assigned customers - no-op
      if (!client || subscriptionsRef.current.has(customerId)) {
        return noop;
      }

      // Already have an ad-hoc subscription - increment ref count
      const existing = adHocSubscriptionsRef.current.get(customerId);
      if (existing) {
        existing.refCount++;
        return () => {
          existing.refCount--;
          if (existing.refCount <= 0) {
            safeRemoveSubscription(client, existing.subscription);
            adHocSubscriptionsRef.current.delete(customerId);
          }
        };
      }

      // Create new subscription safely
      const channel = `customer:${customerId}`;
      const subscription = safeCreateSubscription(client, channel, handlePublication);

      // If subscription creation failed, return no-op (app continues working without real-time updates)
      if (!subscription) {
        return noop;
      }

      adHocSubscriptionsRef.current.set(customerId, { subscription, refCount: 1 });

      return () => {
        const entry = adHocSubscriptionsRef.current.get(customerId);
        if (entry) {
          entry.refCount--;
          if (entry.refCount <= 0) {
            safeRemoveSubscription(client, entry.subscription);
            adHocSubscriptionsRef.current.delete(customerId);
          }
        }
      };
    },
    [handlePublication],
  );

  // Subscribe to staff channel for AI agent response notifications
  const subscribeToStaffChannel = useCallback(
    (staffId: number, callback: GPAgentResponseCallback) => {
      const client = clientRef.current;

      if (!client) {
        return noop;
      }

      const channel = `staff:${staffId}`;

      const handleStaffPublication = (ctx: {data: unknown}) => {
        const data = ctx.data as GPAgentResponseNotification | null;

        if (!data || typeof data !== 'object' || data.type !== 'gp_agent_response') {
          return;
        }

        try {
          // thread_id can come as string or number from Centrifugo
          const threadId = typeof data.thread_id === 'string' ?
            parseInt(data.thread_id, 10) :
            data.thread_id;
          callback(threadId, data.error);
        } catch (error) {
          console.warn('[Centrifugo] Staff channel callback execution failed:', error);
        }
      };

      const subscription = safeCreateSubscription(client, channel, handleStaffPublication);

      if (!subscription) {
        return noop;
      }

      return () => {
        safeRemoveSubscription(client, subscription);
      };
    },
    [],
  );

  // Memoize context value to prevent unnecessary re-renders in consumers
  const contextValue = useMemo(
    () => ({ onDialogueUpdated, onActivitiesUpdated, subscribeToCustomer, subscribeToStaffChannel, isConnected }),
    [onDialogueUpdated, onActivitiesUpdated, subscribeToCustomer, subscribeToStaffChannel, isConnected],
  );

  return <CentrifugoContext.Provider value={contextValue}>{children}</CentrifugoContext.Provider>;
};

export default CentrifugoContextProvider;
